import React, { useEffect, useState } from 'react';
import Chart from 'react-apexcharts';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

// import { ReactComponent as NavigateArrow } from '../../../../assets/icons/dashboard/src/assets/icons/dashboard/navigate-arrow.svg';
import { chartTimePeriod } from '../../../../config/enum';
import BatteryLoader from '../../../../shared-components/battery-loader/BatteryLoader';
import CustomToggleButton from '../../../../shared-components/custom-toggle-button/CustomToggleButton';
import dashboard from '../../../../utils/api/partner/dashboard';

interface ChartDataProperties {
  label: string;
  data: number[];
}

/**
 * Device utilization report chart
 * @returns {any} The result
 */
function DeviceUtilizationChart() {
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();
  const [barGraphData, setBarGraphData] = useState<ChartDataProperties[]>([]);
  const [chartOptions, setChartOptions] = useState<ApexCharts.ApexOptions>({
    chart: {
      type: 'heatmap',
      toolbar: { show: false },
    },
    tooltip: {
      y: {
        /**
         * Formats tooltip values to include unit "kW"
         * @param {number} value - The numeric value from the chart
         * @returns {string} - The formatted value with unit
         */
        formatter(value) {
          return `${value} kW`;
        },
      },
    },
    plotOptions: {
      heatmap: {
        shadeIntensity: 0.5,
        radius: 1,
        useFillColorAsStroke: true,
        colorScale: {
          ranges: [],
        },
      },
    },
    dataLabels: {
      enabled: false,
    },
    legend: {
      show: false, // Hide the legend since we're using a custom one
      position: 'bottom', // If you want to move the legend to the bottom as well
    },
  });
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const activeRangeReference = React.useRef<
    'last24hours' | 'last7days' | 'lastMonth' | 'last12months'
  >('last24hours');

  /**
   * Calculate median value and update chart options
   * @param {any} chartData - The chartData
   */
  const findMedianValueFromData = (chartData: ChartDataProperties[]) => {
    const allValues = chartData
      .flatMap((month) => month.data)
      .filter((value) => typeof value === 'number' && !Number.isNaN(value) && value !== 0);

    if (allValues.length === 0) {
      return;
    }

    const sorted = [...allValues].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    const median = sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
    const roundedMedian = Math.round(median);

    // Update chart options with new color scale
    setChartOptions((previous) => ({
      ...previous,
      plotOptions: {
        heatmap: {
          ...previous.plotOptions?.heatmap,
          colorScale: {
            ranges: [
              { from: 0, to: roundedMedian, name: t('low'), color: '#2AA700' },
              {
                from: roundedMedian,
                to: Math.max(...allValues) || roundedMedian * 2,
                name: t('high'),
                color: '#E41010',
              },
            ],
          },
        },
      },
    }));
  };

  /**
   * Fetch power consumed data based on time range
   * @param {any} range - The range
   */
  const fetchPowerConsumedReport = async (
    range: 'last24hours' | 'last7days' | 'lastMonth' | 'last12months',
  ) => {
    activeRangeReference.current = range;
    try {
      setIsLoading(true);
      const response = await dashboard.getDeviceUtilizationReport({
        timePeriod: range,
      });

      // ❌ If user already switched to another range, ignore this response
      if (activeRangeReference.current !== range) return;

      // ✅ Otherwise, update the chart with the right data
      if (range === 'last12months') {
        setBarGraphData(response?.data || []);
      } else {
        setBarGraphData(
          response?.data?.map((eachData: any) => ({
            ...eachData,
            name: eachData?.name?.replace(/Z$/, ''),
          })),
        );
      }
      findMedianValueFromData(response?.data || []);
      setIsLoading(false);
    } catch (error) {
      console.log(error);

      // only stop loading if this request is still the active one
      if (activeRangeReference.current === range) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchPowerConsumedReport('last24hours');
  }, []);

  return (
    <div className="dashboard-device-utilization-chart-wrap-partner">
      <div className="dashboard-device-utilization-chart-header">
        <div className="header-title">
          <h6>{t('utilizationDevice')}</h6>
          <span
            className="header-arrow"
            role="presentation"
            onClick={() => navigate('/ev-admin/operations/chargers')}>
            {/* <NavigateArrow /> */}
          </span>
        </div>
        <CustomToggleButton
          labels={chartTimePeriod.map((duration) => t(duration.label))}
          onToggle={(index, label) => {
            const selectedDuration = chartTimePeriod.find(
              (duration) => t(duration.label) === label,
            );
            fetchPowerConsumedReport(selectedDuration?.value || 'last12months');
            setIsLoading(true);
          }}
          defaultActiveIndex={0}
          type="chart"
        />
      </div>
      {isLoading ? (
        <center className="device-utilization-loader">
          <BatteryLoader />
        </center>
      ) : (
        <div className="dashboard-device-utilization-chart-container">
          <Chart options={chartOptions} series={barGraphData} type="heatmap" height={350} />
          <div className="heatmap-legend">
            <div className="legend-item">
              <span className="legend-color low"> </span>
              {t('low')}
            </div>

            <div className="legend-item">
              <span className="legend-color high"> </span>
              {t('high')}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DeviceUtilizationChart;
